"use client";

import {
  Dispatch,
  SetStateAction,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { ReactLenis, useLenis } from "@studio-freight/react-lenis";

import { MenuProps } from "./wordpress/_types/blocksStyle";
import { PopupConfig } from "@/components/common/Popup";
import React from "react";
import ScrollTrigger from "gsap/ScrollTrigger";
import gsap from "gsap";
import { useRouter, usePathname } from "next/navigation";
import getMenus from "./actions/menusAction";

interface ContextProps {
  i18n?: { [x: string]: Record<string, string> | string } | any;
  lang?: string;
  translatedUri?: { [x: string]: Record<string, string> } | null;
  setTranslatedUri?:
    | Dispatch<
        SetStateAction<{
          [x: string]: Record<string, string> | undefined | null;
        } | null>
      >
    | any;
  setMenus?: Dispatch<SetStateAction<any>>;
  setLang?: Dispatch<SetStateAction<string | undefined>>;
  menus?: {
    [x: string]: MenuProps[];
  };
  themeOptions?: {
    socials?: Record<string, string>[];
    popups?: PopupConfig[];
  };
  dict?: Record<string, Record<string, string> | any>;
  lenis?: Record<string, Record<string, string> | any>;
  handleMenusPerLanguage?: (locale: string) => void;
  footer?: any;
}

const GlobalContext = createContext<ContextProps>({
  i18n: {},
  lang: "",
  translatedUri: {},
  setTranslatedUri: undefined,
  themeOptions: {},
  dict: {},
  lenis: {},
  footer: {},
  setLang: function (value: SetStateAction<string | undefined>): void {
    throw new Error("Function not implemented.");
  },
  setMenus: function (value: SetStateAction<string | undefined>): void {
    throw new Error("Function not implemented.");
  },
  handleMenusPerLanguage: function (): void {
    throw new Error("Function not implemented.");
  },
});

export const GlobalContextProvider = ({
  children,
  value,
}: {
  children: React.ReactNode;
  value: ContextProps;
}) => {
  gsap.registerPlugin(ScrollTrigger);

  ScrollTrigger.config({ ignoreMobileResize: true });
  const [translatedUri, setTranslatedUri] = useState();
  const [lang, setLang] = useState(value.lang);
  const i18n = value.i18n || {};
  // Rendered on the server by the root layout; null when WordPress failed there.
  const [menus, setMenus] = useState(value.menus || undefined);
  const [dict, setDict] = useState(value.dict || undefined);

  const [footer, setFooter] = useState(value.footer || undefined);
  const [themeOptions, setThemeOptions] = useState(value.themeOptions || {});
  // Language the current menus/dict/footer belong to
  const loadedLang = useRef(value.menus && value.dict && value.footer ? value.lang : undefined);

  const lenis = useLenis(() => {
    // called every scroll
  });

  useEffect(() => {
    // The server already rendered this language; only fetch on a language change
    // or to retry what the server couldn't load.
    if (lang && lang === loadedLang.current) return;
    handleMenusPerLanguage(lang || "en");
  }, [lang]);

  const router = useRouter();

  const handleMenusPerLanguage = async (locale: string) => {
    translatedUri && router.replace(translatedUri[locale]);
    try {
      const newMenus = await getMenus(locale);
      // Keep what we already have when a request fails, so the header never
      // goes empty because WordPress didn't answer.
      if (newMenus.menus) setMenus(newMenus.menus);
      if (newMenus.footer) setFooter(newMenus.footer);
      if (newMenus.dict) setDict(newMenus.dict);
      // Update themeOptions when language changes
      if (newMenus.themeOptions) setThemeOptions(newMenus.themeOptions);
      if (newMenus.menus && newMenus.footer && newMenus.dict) loadedLang.current = locale;
    } catch (error) {
      console.error("Error fetching menus:", error);
    }
  };

  const GlobalContextValues = useMemo(() => {
    return {
      i18n,
      lang,
      translatedUri,
      menus,
      themeOptions,
      dict,
      lenis,
      footer,
      setTranslatedUri,
      setLang,
      setMenus,
      handleMenusPerLanguage,
    };
  }, [
    i18n,
    lang,
    translatedUri,
    menus,
    themeOptions,
    dict,
    lenis,
    footer,
    setTranslatedUri,
    setLang,
    setMenus,
    handleMenusPerLanguage,
  ]);

  return (
    <GlobalContext.Provider value={GlobalContextValues}>
      <ReactLenis
        root
        options={{
          duration: 1.2,
          easing: (t: number) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
        }}
      >
        {children}
      </ReactLenis>
    </GlobalContext.Provider>
  );
};

export const useGlobalContext = () => useContext(GlobalContext);
